﻿import {
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery
} from '@tanstack/react-query';
import axios from 'axios';

import {
  ALL_ORGS_QUERY_KEY,
  ORGANIZATION_API_URL,
  ORGANIZATION_SETTINGS_API_URL,
  ORG_BY_ID_QUERY_KEY,
  ORG_BY_ID_SIMPLIFIED_QUERY_KEY
} from '@/constants';
import { apiUrl, getQueryParams } from '@/helpers';
import {
  CreateEditOrganization,
  ErrorResponse,
  Options,
  UpdateOrganizationCustomResolutionPayload,
  UpdateOrganizationLimitsPayload,
  UpdateOrganizationSettingsPayload
} from '@/types';

export const OrganizationEndpoints = {
  getAllOrganizations: (
    pageSize: number,
    cursor: number,
    searchQuery?: string,
    enabled?: boolean
  ) =>
    axios.get(
      `${apiUrl()}${ORGANIZATION_API_URL}${getQueryParams({
        'paging.PageSize': pageSize as number,
        'paging.Cursor': cursor as number,
        'search.Text': searchQuery as string,
        'filter.Enabled': enabled as boolean
      })}`
    ),
  createOrganization: (payload: CreateEditOrganization) =>
    axios.post(`${apiUrl()}${ORGANIZATION_API_URL}`, payload),
  updateOrganization: (payload: CreateEditOrganization) =>
    axios.put(`${apiUrl()}${ORGANIZATION_API_URL}`, payload),
  getOrganizationById: (id: number) => axios.get(`${apiUrl()}${ORGANIZATION_API_URL}/${id}`),
  getOrganizationByIdSimplified: (id: number) =>
    axios.get(`${apiUrl()}${ORGANIZATION_API_URL}/${id}/simplified`),
  deleteOrganization: (id: number) => axios.delete(`${apiUrl()}${ORGANIZATION_API_URL}/${id}`),
  getOrganizationParentOptions: (id: number) =>
    axios.get(`${apiUrl()}${ORGANIZATION_API_URL}/parentOptions/${id}`),
  updateOrganizationSettings: (payload: UpdateOrganizationSettingsPayload) =>
    axios.patch(`${apiUrl()}${ORGANIZATION_SETTINGS_API_URL}`, payload),
  calculateOrganizationStorage: (organizationId: number) =>
    axios.patch(`${apiUrl()}${ORGANIZATION_SETTINGS_API_URL}/storage/calculate/${organizationId}`),
  updateOrganizationLimits: (payload: UpdateOrganizationLimitsPayload) =>
    axios.patch(`${apiUrl()}${ORGANIZATION_SETTINGS_API_URL}/limits`, payload),
  updateOrganizationCustomResolution: (payload: UpdateOrganizationCustomResolutionPayload) =>
    axios.patch(`${apiUrl()}${ORGANIZATION_SETTINGS_API_URL}/custom-resolution`, payload)
};

export const useGetAllOrganizations = (
  pageSize: number,
  searchQuery?: string,
  enabled?: boolean,
  hasPermission?: boolean,
  userId?: number
) => {
  return useInfiniteQuery(
    [ALL_ORGS_QUERY_KEY, { pageSize, searchQuery, enabled, userId }],
    ({ pageParam }) =>
      OrganizationEndpoints.getAllOrganizations(pageSize, pageParam, searchQuery, enabled),
    {
      keepPreviousData: true,
      enabled: hasPermission,
      getNextPageParam: (lastPage) => {
        if (lastPage && lastPage.data?.nextCursor !== undefined) {
          return lastPage.data?.nextCursor;
        }
        return undefined;
      }
    }
  );
};

export const useGetOrganizationByIdQuery = (id: number, options?: Options) => {
  return useQuery(
    [ORG_BY_ID_QUERY_KEY, { id }],
    () => OrganizationEndpoints.getOrganizationById(id),
    {
      enabled: !!id,
      retry: false,
      onError: (error) => {
        options?.onError(error as ErrorResponse);
      }
    }
  );
};

export const useGetOrganizationByIdSimplifiedQuery = (
  id: number,
  options?: Options,
  isEnabled = true
) => {
  return useQuery(
    [ORG_BY_ID_SIMPLIFIED_QUERY_KEY, { id }],
    () => OrganizationEndpoints.getOrganizationByIdSimplified(id),
    {
      enabled: !!id && isEnabled,
      retry: false,
      onError: (error) => {
        options?.onError(error as ErrorResponse);
      }
    }
  );
};

export const useGetOrganizationsByIdsSimplifiedQueries = (ids: number[], isEnabled = true) => {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: [ORG_BY_ID_SIMPLIFIED_QUERY_KEY, { id }],
      queryFn: () => OrganizationEndpoints.getOrganizationByIdSimplified(id),
      enabled: !!id && isEnabled,
      retry: false
    }))
  });
};

export const useCreateOrganizationMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(OrganizationEndpoints.createOrganization, {
    onSuccess: (response) => {
      options.onSuccess(response);
      queryClient.invalidateQueries([ALL_ORGS_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useUpdateOrganizationMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(OrganizationEndpoints.updateOrganization, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ALL_ORGS_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useDeleteOrganizationMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(OrganizationEndpoints.deleteOrganization, {
    onSuccess: () => {
      options?.onSuccess();
      queryClient.invalidateQueries([ALL_ORGS_QUERY_KEY]);
    },
    onError: (error) => {
      options?.onError(error as ErrorResponse);
    }
  });
};

export const useGetOrganizationParentOptions = (id: number) => {
  return useQuery(
    ['organizationParentOptions', { id }],
    () => OrganizationEndpoints.getOrganizationParentOptions(id),
    {
      enabled: id !== 0, // Enable for both new organizations (-1) and existing ones (positive numbers)
      retry: false
    }
  );
};

export const useUpdateOrganizationSettingsMutation = (
  queryClient: QueryClient,
  options: Options
) => {
  return useMutation(OrganizationEndpoints.updateOrganizationSettings, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ORGANIZATION_API_URL]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useCalculateOrganizationStorageMutation = (
  queryClient: QueryClient,
  options: Options
) => {
  return useMutation(OrganizationEndpoints.calculateOrganizationStorage, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ORGANIZATION_API_URL]);
      queryClient.invalidateQueries([ORG_BY_ID_QUERY_KEY]);
      queryClient.invalidateQueries([ORG_BY_ID_SIMPLIFIED_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useUpdateOrganizationLimitsMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(OrganizationEndpoints.updateOrganizationLimits, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ORGANIZATION_API_URL]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useUpdateOrganizationCustomResolutionMutation = (
  queryClient: QueryClient,
  options: Options
) => {
  return useMutation(OrganizationEndpoints.updateOrganizationCustomResolution, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([ORGANIZATION_API_URL]);
      queryClient.invalidateQueries([ORG_BY_ID_QUERY_KEY]);
      queryClient.invalidateQueries([ORG_BY_ID_SIMPLIFIED_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};
